import api from './api';
import { Producer } from './producerService';
import { Grape } from './grapeService';
import { WineRegion } from './wineRegionService';

// ── Enum types ────────────────────────────────────────────────────────────────
export type WineColor = 'RED' | 'WHITE' | 'ROSE' | 'ORANGE' | 'SPARKLING' | 'DESSERT' | 'FORTIFIED';
export type WineType = 'WINE' | 'SPIRITS' | 'BEER' | 'OTHER';
export type WineSubType = 'STILL' | 'SPARKLING' | 'FORTIFIED' | 'DESSERT' | 'SWEET' | 'NATURAL';
export type VintageConfig = 'SEQUENTIAL' | 'NON_SEQUENTIAL';

const API_ENDPOINTS = {
  WINE_NAME: '/admin/wine-name',
  PRODUCER: '/admin/producer',
  GRAPE: '/admin/grape',
  WINE_REGION: '/admin/wine-region',
  REGION_SUGGESTIONS: '/admin/wine-region-search/suggestions',
};

// Lightweight references used within WineName (populated)
export interface WineNameProducer {
  _id: string;
  name: string;
  fullName?: string;
  needsReview?: boolean;
}

export interface WineNameRegion {
  _id: string;
  name: string;
  hierarchyNames?: string[];
  needsReview?: boolean;
}

export interface WineNameGrape {
  _id: string;
  primaryName: string;
  color?: 'RED' | 'WHITE' | 'PINK_ROSE';
  needsReview?: boolean;
}

export interface WineName {
  _id: string;
  /**
   * SIMPLE-WINE.2 §3 — NULLABLE, and it always was.
   *
   * A provisional wine name is created with producer and region null and
   * waits for a reviewer to say what wine it is. This field was typed
   * non-nullable, so every caller was told it could dereference freely; the
   * wine-name editor did exactly that and rendered blank for precisely the
   * records a reviewer opens it to fix. The type was the reason nothing
   * warned.
   */
  producer: WineNameProducer | null;
  region?: WineNameRegion | null;  // optional AND nullable, for the same reason
  labelGrapes?: WineNameGrape[];
  specialName?: string;
  color?: WineColor;
  type?: WineType;
  subType?: WineSubType;
  designation?: string;
  classification?: string;
  vintageConfig?: VintageConfig;
  firstVintage?: number;
  finalVintage?: number;
  lwinId?: string;
  displayName: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WineNameFormData {
  producerId: string;
  regionId: string;            // required — region is now mandatory
  labelGrapeIds?: string[];
  specialName?: string;
  color: WineColor;           // required
  type: WineType;              // required, default WINE
  subType?: WineSubType;
  designation?: string;
  classification?: string;
  vintageConfig?: VintageConfig;
  firstVintage?: number;
  finalVintage?: number;
  lwinId?: string;
  active: boolean;
  needsReview?: boolean;
}

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existing?: WineName;
}

export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// Autocomplete option types used in form
export interface ProducerOption {
  _id: string;
  name: string;
  fullName?: string;
  needsReview?: boolean;
  __isNew__?: boolean;
  inputValue?: string;
}

export interface RegionOption {
  _id: string;
  name: string;
  hierarchyNames?: string[];
  needsReview?: boolean;
  __isNew__?: boolean;
  inputValue?: string;
}

export interface GrapeOption {
  _id: string;
  primaryName: string;
  color?: 'RED' | 'WHITE' | 'PINK_ROSE';
  needsReview?: boolean;
  __isNew__?: boolean;
  inputValue?: string;
}

const wineNameService = {
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    active?: boolean;
  }): Promise<PaginatedResponse<WineName>> => {
    const response = await api.get(API_ENDPOINTS.WINE_NAME, { params });
    return response.data;
  },

  getById: async (id: string): Promise<WineName> => {
    const response = await api.get(`${API_ENDPOINTS.WINE_NAME}/${id}`);
    return response.data;
  },

  create: async (data: WineNameFormData): Promise<WineName> => {
    const response = await api.post(API_ENDPOINTS.WINE_NAME, data);
    return response.data;
  },

  update: async (id: string, data: Partial<WineNameFormData>): Promise<WineName> => {
    const response = await api.put(`${API_ENDPOINTS.WINE_NAME}/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`${API_ENDPOINTS.WINE_NAME}/${id}`);
  },

  checkDuplicate: async (data: {
    producerId: string;
    regionId?: string;
    specialName?: string;
    labelGrapeIds?: string[];
  }): Promise<DuplicateCheckResult> => {
    const response = await api.post(`${API_ENDPOINTS.WINE_NAME}/check-duplicate`, data);
    return response.data;
  },

  // Search helpers for form autocompletes
  searchProducers: async (q: string): Promise<ProducerOption[]> => {
    const response = await api.get(API_ENDPOINTS.PRODUCER, {
      params: { q: q.trim(), pageSize: 20 },
    });
    const data: Producer[] = response.data?.data ?? [];
    return data.map((p) => ({ _id: p._id, name: p.name, fullName: p.fullName }));
  },

  searchRegions: async (q: string): Promise<RegionOption[]> => {
    const response = await api.get(API_ENDPOINTS.REGION_SUGGESTIONS, {
      params: { q: q.trim(), limit: 10 },
    });
    const suggestions = response.data?.data?.suggestions ?? [];
    return suggestions.map((s: any) => ({
      _id: s.id,
      name: s.name,
      hierarchyNames: s.hierarchyNames,
    }));
  },

  searchGrapes: async (q: string): Promise<GrapeOption[]> => {
    const response = await api.get(API_ENDPOINTS.GRAPE, {
      params: { q: q.trim(), pageSize: 20 },
    });
    const data: Grape[] = response.data?.data ?? [];
    return data.map((g) => ({ _id: g._id, primaryName: g.primaryName, color: g.color }));
  },

  // Create-on-the-fly helpers — create minimal records marked needsReview
  createProducerOnFly: async (name: string): Promise<ProducerOption> => {
    const response = await api.post(API_ENDPOINTS.PRODUCER, {
      name,
      fullName: name,
      country: 'UNKNOWN',
      needsReview: true,
    });
    const p: Producer = response.data;
    return { _id: p._id, name: p.name, fullName: p.fullName, needsReview: true };
  },

  createRegionOnFly: async (name: string): Promise<RegionOption> => {
    const response = await api.post(API_ENDPOINTS.WINE_REGION, {
      name,
      needsReview: true,
    });
    const r: WineRegion = response.data;
    return { _id: r._id, name: r.name, needsReview: true };
  },

  createGrapeOnFly: async (primaryName: string): Promise<GrapeOption> => {
    try {
      const response = await api.post(API_ENDPOINTS.GRAPE, {
        primaryName,
        aliases: [],
        color: 'RED',
        needsReview: true,
      });
      const g: Grape = response.data;
      return { _id: g._id, primaryName: g.primaryName, color: g.color, needsReview: true };
    } catch (err: any) {
      // HOTFIX-UI-3 (fix 2) — the grape may already exist (409, case-insensitive
      // duplicate). Recover by selecting the existing grape instead of dead-ending
      // the selector with a generic failure.
      if (err?.response?.status === 409) {
        const match = (await wineNameService.searchGrapes(primaryName)).find(
          o => o.primaryName.toLowerCase() === primaryName.trim().toLowerCase(),
        );
        if (match) return match;
      }
      throw err;
    }
  },
};

export default wineNameService;
